import React, { useEffect, useRef, useState } from "react";
import { FiCamera, FiMaximize2, FiX } from "react-icons/fi";
import {
  listMonthlyPhotosForStudent,
  AvaliationPhotoMonthRecord,
} from "../../../services/avaliationPhotoMonth";
import {
  EmptyState,
  CARD_BASE_CLASS,
} from "../StudentManagementModal/StudentManagementModalComponents";

const THUMB_SIZE = 80;
const PHOTO_KEYS = [
  { key: "frente" as const, label: "Frente", urlKey: "url_frente" as const },
  { key: "costas" as const, label: "Costas", urlKey: "url_costas" as const },
  {
    key: "lateral_direita" as const,
    label: "Lateral direita",
    urlKey: "url_lateral_direita" as const,
  },
  {
    key: "lateral_esquerda" as const,
    label: "Lateral esquerda",
    urlKey: "url_lateral_esquerda" as const,
  },
] as const;

function formatMonthTitle(yearMonth: string): string {
  if (!yearMonth) return "";
  const [y, m] = yearMonth.split("-");
  const date = new Date(Number(y), Number(m || 1) - 1, 1);
  const str = date.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
  return str.charAt(0).toUpperCase() + str.slice(1);
}

interface StudentMonthlyPhotosTabProps {
  readonly studentId: string;
  readonly professionalId: string;
}

type ExpandedPhoto = {
  yearMonth: string;
  key: (typeof PHOTO_KEYS)[number]["key"];
  url: string;
  label: string;
};

export default function StudentMonthlyPhotosTab({
  studentId,
  professionalId,
}: StudentMonthlyPhotosTabProps) {
  const [list, setList] = useState<AvaliationPhotoMonthRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPhoto, setExpandedPhoto] = useState<ExpandedPhoto | null>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = () => {
    if (!fullscreenRef.current) return;
    if (!document.fullscreenElement) {
      fullscreenRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (!studentId) {
      setList([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listMonthlyPhotosForStudent(studentId)
      .then((data) => {
        setList(data);
      })
      .catch(() => {
        setList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [studentId]);

  useEffect(() => {
    if (!expandedPhoto) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (document.fullscreenElement) document.exitFullscreen();
        else setExpandedPhoto(null);
      }
    };
    const cleanup = () => {
      if (document.fullscreenElement) document.exitFullscreen();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      cleanup();
    };
  }, [expandedPhoto]);

  const sortedByMonth = [...list].sort(
    (a, b) => (b.year_month || "").localeCompare(a.year_month || "")
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-7 w-48 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className={CARD_BASE_CLASS}>
              <div className="h-5 w-32 mb-3 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((j) => (
                  <div
                    key={j}
                    className="rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"
                    style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <EmptyState
        icon={FiCamera}
        title="Nenhuma foto de avaliação mensal ainda"
        description="O aluno pode enviar fotos de avaliação mensal pelo app. Elas aparecerão aqui agrupadas por mês."
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Fotos enviadas pelo aluno, agrupadas por mês. Clique em uma miniatura
        para ampliar.
      </p>
      <div className="space-y-4">
        {sortedByMonth.map((record) => (
          <div key={record.id} className={CARD_BASE_CLASS}>
            <h4 className="mb-3 text-base font-bold text-gray-900 dark:text-white">
              {formatMonthTitle(record.year_month || "")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {PHOTO_KEYS.map(({ key, label, urlKey }) => {
                const url = record[urlKey];
                if (!url) return null;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setExpandedPhoto({ yearMonth: record.year_month || "", key, url, label })}
                    className="overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
                    aria-label={`Ver ${label} em tamanho maior`}
                  >
                    <img
                      src={url}
                      alt={label}
                      className="h-full w-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {expandedPhoto && (
        <dialog
          open
          className="fixed inset-0 z-[100] m-0 flex max-h-full max-w-full items-center justify-center border-0 bg-black/80 p-4 [&::backdrop]:bg-black/80"
          aria-label="Foto em tamanho maior"
        >
          <div
            ref={fullscreenRef}
            className="flex h-full min-h-0 w-full flex-col items-center justify-center bg-black"
          >
            <div className="absolute right-4 top-4 z-10 flex gap-2">
              <button
                type="button"
                onClick={toggleFullscreen}
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                aria-label="Tela cheia"
              >
                <FiMaximize2 className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (document.fullscreenElement) document.exitFullscreen();
                  setExpandedPhoto(null);
                }}
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                aria-label="Fechar"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            <p className="absolute left-4 top-4 z-10 text-sm font-medium text-white">
              {formatMonthTitle(expandedPhoto.yearMonth)} – {expandedPhoto.label}
            </p>
            <div className="flex flex-1 items-center justify-center p-4 pt-12">
              <img
                src={expandedPhoto.url}
                alt={expandedPhoto.label}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
}
